import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import ConfirmDialog from "components/common/ConfirmDialog";
import CustomButton from "components/common/CustomButton";
import {
  connectionLabel,
  useDeleteConnectionMutation,
  useGetConnectionConfigSchemaQuery,
  useGetConnectionQuery,
} from "features/integrationsBeta/connections";
import type { ReadinessIssue } from "features/integrationsBeta/integrationsBeta";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useState } from "react";
import { formatApiError } from "utils/formatApiError";
import CredentialsManager from "./CredentialsManager";
import JsonSchemaForm from "./JsonSchemaForm";
import PayloadView from "./PayloadView";
import { usePendingChanges } from "./pending/PendingChangesContext";

type Json = Record<string, unknown>;

export default function ConnectionCard({
  connectionId,
  issues,
  deletable = false,
}: {
  connectionId: number;
  issues?: ReadinessIssue[];
  /** Show a Delete action (used for connections no pipeline references). */
  deletable?: boolean;
}) {
  const dispatch = useAppDispatch();
  const { isSuperuser } = usePlatformRole();
  const { data: connection, isLoading } = useGetConnectionQuery(connectionId);
  const { data: schemaResp } = useGetConnectionConfigSchemaQuery(connectionId);
  const { stage, getPending } = usePendingChanges();

  const [editOpen, setEditOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [draft, setDraft] = useState<Json>({});
  const [rawDraft, setRawDraft] = useState("");
  const [rawError, setRawError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteConnection, { isLoading: deleting }] =
    useDeleteConnectionMutation();

  if (isLoading || !connection) {
    return <Skeleton variant="rounded" height={96} />;
  }

  const schema = (schemaResp?.config_schema as Json | null) ?? null;

  const pendingName = getPending("connection", connectionId, "name");
  const pendingConfig = getPending("connection", connectionId, "config");
  const effectiveName =
    (pendingName?.newValue as string | undefined) ?? connection.name ?? "";
  const effectiveConfig =
    (pendingConfig?.newValue as Json | undefined) ??
    (connection.config as Json) ??
    {};

  const openEdit = () => {
    setNameDraft(effectiveName);
    setDraft(effectiveConfig);
    setRawDraft(JSON.stringify(effectiveConfig, null, 2));
    setRawError(null);
    setEditOpen(true);
  };

  const handleApply = () => {
    let config: Json;
    if (schema) {
      config = draft;
    } else {
      try {
        config = rawDraft.trim() ? JSON.parse(rawDraft) : {};
      } catch {
        setRawError("Invalid JSON.");
        return;
      }
    }
    const base = {
      entityType: "connection" as const,
      entityId: connectionId,
      section: "connections" as const,
      entityLabel: `Connection "${connectionLabel(connection)}"`,
    };
    // Unchanged fields auto-drop, so a name-only edit stages one record.
    stage({
      ...base,
      field: "name",
      fieldLabel: "Name",
      valueKind: "text",
      oldValue: connection.name ?? "",
      newValue: nameDraft.trim(),
    });
    stage({
      ...base,
      field: "config",
      fieldLabel: "Configuration",
      valueKind: "config",
      oldValue: (connection.config as Json) ?? {},
      newValue: config,
    });
    setEditOpen(false);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {connectionLabel(connection)}
          </Typography>
          {connection.name && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {connection.provider_name}
            </Typography>
          )}
        </Box>
        {(pendingName || pendingConfig) && (
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label="Unsaved changes"
          />
        )}
        <Chip label={connection.type} size="small" variant="outlined" />
        {isSuperuser && (
          <Tooltip title="Edit connection">
            <IconButton
              size="small"
              onClick={openEdit}
              aria-label="Edit connection"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {isSuperuser && deletable && (
          <Tooltip title="Delete connection">
            <IconButton
              size="small"
              onClick={() => setConfirmDelete(true)}
              aria-label="Delete connection"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {issues && issues.length > 0 && (
        <Alert
          severity={
            issues.some((i) => i.severity === "error") ? "error" : "warning"
          }
          sx={{ mb: 1.5 }}
        >
          {issues.map((issue, index) => (
            <Typography variant="body2" key={`${issue.code}-${index}`}>
              {issue.message}
            </Typography>
          ))}
        </Alert>
      )}

      <PayloadView
        value={effectiveConfig}
        emptyHint="No parameters"
        boxed
        title="Configuration"
        copyable
      />

      <CredentialsManager connectionId={connectionId} />

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`Edit ${connection.provider_name} (${connection.type})`}
        maxWidth="sm"
        showCloseIcon
        actions={
          <>
            <CustomButton fullWidth={false} onClick={() => setEditOpen(false)}>
              Cancel
            </CustomButton>
            <CustomButton fullWidth={false} onClick={handleApply}>
              Apply
            </CustomButton>
          </>
        }
      >
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            helperText={
              "Optional label to tell sibling connections apart (e.g. " +
              "'Outbound SFTP'). New credentials embed it in their AWS " +
              "secret path; already-created secrets keep their paths."
            }
          />
          {schema ? (
            <JsonSchemaForm schema={schema} value={draft} onChange={setDraft} />
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={6}
              label="Config (JSON)"
              value={rawDraft}
              error={!!rawError}
              helperText={rawError ?? "No schema available — edit raw JSON."}
              onChange={(e) => {
                setRawDraft(e.target.value);
                setRawError(null);
              }}
              sx={{ fontFamily: "monospace" }}
            />
          )}
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete connection"
        message={`Delete connection "${connectionLabel(connection)}"? Its credentials are removed from AWS Secrets Manager as well. This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={async () => {
          try {
            await deleteConnection(connectionId).unwrap();
            dispatch(
              pushNotification({
                severity: "success",
                message: "Connection deleted.",
              }),
            );
          } catch (err) {
            dispatch(
              pushNotification({
                severity: "error",
                message: formatApiError(
                  (err as { data?: unknown })?.data,
                  "Could not delete the connection.",
                ),
              }),
            );
          } finally {
            setConfirmDelete(false);
          }
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Paper>
  );
}
